import {
	type FormEvent,
	useCallback,
	useEffect,
	useRef,
	useState,
} from "react";
import { Link, NavLink, useNavigate, useSearchParams } from "react-router";
import { ARCHETYPE_LABELS } from "../types/Game";
import type { Game } from "../types/Game";
import { searchGames } from "../data/placeholderGames";

/**
 * Global top nav. Logo + browse links + search with debounced live dropdown.
 * Desktop: full search bar + Create CTA.
 * Mobile: compact search, browse links handled by MobileNav at bottom.
 */
export function Nav() {
	const navigate = useNavigate();
	const [params] = useSearchParams();
	const initialQuery = params.get("q") ?? "";
	const [query, setQuery] = useState(initialQuery);
	const [results, setResults] = useState<Game[]>([]);
	const [showDropdown, setShowDropdown] = useState(false);
	const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const containerRef = useRef<HTMLDivElement>(null);

	// Sync input with URL params (back/forward navigation)
	useEffect(() => {
		setQuery(params.get("q") ?? "");
		setShowDropdown(false);
	}, [params]);

	// Debounced live search — 200ms delay
	const handleChange = useCallback((value: string) => {
		setQuery(value);
		if (debounceRef.current) clearTimeout(debounceRef.current);
		const trimmed = value.trim();
		if (!trimmed) {
			setResults([]);
			setShowDropdown(false);
			return;
		}
		debounceRef.current = setTimeout(() => {
			const matches = searchGames(trimmed).slice(0, 5);
			setResults(matches);
			setShowDropdown(matches.length > 0);
		}, 200);
	}, []);

	// Click outside closes dropdown
	useEffect(() => {
		function handleClickOutside(e: MouseEvent) {
			if (
				containerRef.current &&
				!containerRef.current.contains(e.target as Node)
			) {
				setShowDropdown(false);
			}
		}
		document.addEventListener("mousedown", handleClickOutside);
		return () =>
			document.removeEventListener("mousedown", handleClickOutside);
	}, []);

	function handleSubmit(e: FormEvent<HTMLFormElement>) {
		e.preventDefault();
		setShowDropdown(false);
		const trimmed = query.trim();
		if (!trimmed) return;
		navigate(`/search?q=${encodeURIComponent(trimmed)}`);
	}

	function handleResultClick(slug: string) {
		setShowDropdown(false);
		setQuery("");
		navigate(`/game/${slug}`);
	}

	function handleKeyDown(e: React.KeyboardEvent) {
		if (e.key === "Escape") {
			setShowDropdown(false);
		}
	}

	return (
		<header className="sticky top-0 z-50 backdrop-blur-xl bg-background/80 border-b border-border">
			<div className="mx-auto max-w-7xl px-4 md:px-6 h-16 flex items-center gap-3 md:gap-6">
				{/* Logo */}
				<Link
					to="/"
					className="flex items-center gap-2.5 shrink-0 font-display font-bold text-lg md:text-xl"
				>
					<span
						className="inline-block h-8 w-8 rounded-lg bg-gradient-to-br from-primary via-accent to-purple"
						aria-hidden="true"
					/>
					<span className="hidden sm:inline">
						<span className="text-ink">Genie</span>
						<span className="text-primary">Worlds</span>
					</span>
				</Link>

				{/* Desktop nav links */}
				<nav className="hidden md:flex items-center gap-1 shrink-0">
					<NavLink
						to="/"
						end
						className={({ isActive }) =>
							`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
								isActive
									? "text-primary"
									: "text-ink-muted hover:text-ink"
							}`
						}
					>
						Home
					</NavLink>
					<NavLink
						to="/browse"
						className={({ isActive }) =>
							`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
								isActive
									? "text-primary"
									: "text-ink-muted hover:text-ink"
							}`
						}
					>
						Browse
					</NavLink>
				</nav>

				{/* Search with live dropdown */}
				<div
					ref={containerRef}
					className="flex-1 relative"
					onKeyDown={handleKeyDown}
				>
					<form onSubmit={handleSubmit} role="search">
						<label htmlFor="nav-search" className="sr-only">
							Search games
						</label>
						<div className="relative">
							<input
								id="nav-search"
								type="search"
								value={query}
								onChange={(e) => handleChange(e.target.value)}
								onFocus={() => {
									if (results.length > 0)
										setShowDropdown(true);
								}}
								placeholder="Search games, creators, tags..."
								className="w-full h-10 pl-10 pr-4 rounded-full bg-surface border border-border text-sm text-ink placeholder:text-ink-faint focus:border-primary/60 focus:bg-surface-raised focus:outline-none transition-all duration-200"
								autoComplete="off"
							/>
							<svg
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								strokeWidth="2"
								className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-faint pointer-events-none"
								aria-hidden="true"
							>
								<circle cx="11" cy="11" r="8" />
								<path d="m21 21-4.3-4.3" />
							</svg>
						</div>
					</form>

					{/* Live search dropdown */}
					{showDropdown && (
						<div className="absolute top-full left-0 right-0 mt-2 bg-surface-raised border border-border rounded-xl overflow-hidden shadow-[0_16px_48px_-12px_rgba(0,0,0,0.6)] animate-dropdown z-50">
							{results.map((g) => (
								<button
									key={g.id}
									type="button"
									onClick={() =>
										handleResultClick(g.slug)
									}
									className="flex items-center gap-3 w-full px-4 py-3 hover:bg-surface-hover transition-colors text-left"
								>
									<span
										className="text-2xl shrink-0 h-10 w-10 rounded-lg flex items-center justify-center"
										style={{
											backgroundColor:
												g.thumbnail.bg,
										}}
									>
										{g.thumbnail.emoji}
									</span>
									<div className="min-w-0 flex-1">
										<div className="text-sm font-semibold text-ink truncate">
											{g.title}
										</div>
										<div className="text-xs text-ink-faint">
											{
												ARCHETYPE_LABELS[
													g.archetype
												]
											}{" "}
											· by {g.creator}
										</div>
									</div>
									<svg
										viewBox="0 0 24 24"
										fill="none"
										stroke="currentColor"
										strokeWidth="2"
										className="h-4 w-4 text-ink-faint shrink-0"
										aria-hidden="true"
									>
										<path d="M9 18l6-6-6-6" />
									</svg>
								</button>
							))}
							<button
								type="button"
								onClick={() => {
									setShowDropdown(false);
									navigate(
										`/search?q=${encodeURIComponent(query.trim())}`,
									);
								}}
								className="flex items-center gap-2 w-full px-4 py-3 border-t border-border text-sm text-primary hover:bg-surface-hover transition-colors"
							>
								See all results for &ldquo;{query.trim()}
								&rdquo;
								<svg
									viewBox="0 0 24 24"
									fill="currentColor"
									className="h-3.5 w-3.5"
									aria-hidden="true"
								>
									<path d="M13 5l7 7-7 7-1.4-1.4L16.2 13H4v-2h12.2l-4.6-4.6z" />
								</svg>
							</button>
						</div>
					)}
				</div>

				{/* Create CTA — desktop only */}
				<a
					href="https://genieforge.io"
					className="hidden md:inline-flex items-center gap-1.5 h-10 px-5 rounded-full bg-gradient-to-r from-primary to-primary/80 text-background text-sm font-bold hover:shadow-[0_0_28px_rgba(0,255,136,0.35)] transition-all duration-300 shrink-0"
				>
					Create
					<svg
						viewBox="0 0 24 24"
						fill="currentColor"
						className="h-3.5 w-3.5"
						aria-hidden="true"
					>
						<path d="M12 2 15 9l7 .8-5.3 4.7L18.2 22 12 18.3 5.8 22l1.5-7.5L2 9.8 9 9z" />
					</svg>
				</a>
			</div>
		</header>
	);
}
